import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectMetadataKeyByRegion } from 'reducers/selectors/metadata';
import { useTranslation } from 'hooks/useTranslation';
import { PAYPAL_ACCOUNT_DETAIL_SCREEN_KEYS } from '../translationKeys';
import AlertModal from 'components/AlertModal';
import { OnboardingLoading } from 'components/onboarding/components/OnboardingLoading';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import ConfirmDeleteSuscriptionsModal from 'components/ConfirmDeleteSuscriptionsModal';
import { deletePayPalPaymentMethod } from 'api/services/qr_payment_methods';
import { useSelectPaymentMethod } from '../../../utils/useSelectPaymentMethod';
import { useInvalidateQrSession } from '../../../utils/useInvalidateQrSession';
import { useBackNavigationStep } from '../../BackNavigation';
import { PayPalCard } from '../components/PayPalCard';
import './PayPalAccountDetailScreen.scss';
import { SelectPaymentMethodButton } from '../../SelectPaymentMethodButton';
import { GATEWAY_CODES } from '../../../constants';

// Copia de containers/Payment/PayPal/screens/PayPalAccountDetailScreen.js.
// Diferencias: el borrado usa deletePayPalPaymentMethod, y qué vista sigue lo
// decide PayPalScreen vía onDeleted.
const PAYPAL_LOGO_KEY = 'mdp_metodosdepago_agregarmdppaypal_logo_paypal';

export const PayPalAccountDetailScreen = ({
  account,
  userToken,
  paymentMethodId,
  displayName,
  sessionUser,
  isMobileFlow,
  deviceParams,
  code,
  onBack,
  onDeleted,
}) => {
  const navigate = useNavigate();
  const assets = useSelector(store => store.asset);

  const { translationMapper } = useTranslation();
  const translatedTexts = useMemo(() => translationMapper(PAYPAL_ACCOUNT_DETAIL_SCREEN_KEYS), [
    translationMapper,
  ]);
  const showPaymentSelector = useSelector(
    selectMetadataKeyByRegion('select_consulted_payment_method_config'),
  )?.enable;

  const [showConfirm, setShowConfirm] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [showSuscriptionsModal, setShowSuscriptionsModal] = useState(false);

  const { isSelecting, selectErrorMessage, clearSelectError, select } =
    useSelectPaymentMethod({
      sessionUser,
      isMobileFlow,
      userToken,
      intermittenceErrorMessage: translatedTexts.INTERMITTENCE_ERROR,
      qrCode: code,
      deviceParams,
    });

  const { invalidateSession } = useInvalidateQrSession({
    sessionUser,
    isMobileFlow,
    code,
    deviceParams,
  });

  useBackNavigationStep(onBack);

  const handleUnlink = async () => {
    setIsUnlinking(true);

    try {
      if (!account?.paymentTokenId) {
        setShowErrorAlert(true);
        return;
      }

      await deletePayPalPaymentMethod(account, { userToken, deviceParams });
      onDeleted?.(account);
    } catch (error) {
      const status = error?.response?.status;
      const errorCode = error?.response?.data?.errors?.[0]?.code;
      const isSubscriptionsConflict =
        status === 412 ||
        (status === 409 && errorCode === 'PGS_DPM_00006');

      if (isSubscriptionsConflict) {
        setShowSuscriptionsModal(true);
      } else {
        setShowErrorAlert(true);
      }
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleCloseErrorAlert = () => {
    setShowErrorAlert(false);
    invalidateSession();
  };

  const handleConfirmSuscriptions = () => {
    setShowSuscriptionsModal(false);
    const region = window.location.pathname.split('/')[1];
    navigate(`/${region}/home/user?micuentafull#micuentavtc`);
  };

  // Selecciona esta cuenta como el instrumento de la transacción QR y cierra
  // la sesión de vinculación (useSelectPaymentMethod).
  const handleSelect = () =>
    select({
      id: paymentMethodId,
      code: GATEWAY_CODES.PAYPAL,
      displayName,
      paymentMethodData: {
        paymentTokenId: account?.paymentTokenId,
        isDefault: true,
      },
    });

  return (
    <div className="paypal-account-detail">
      <OnboardingLoading show={isSelecting} />

      <header className="paypal-account-detail__header">
        {assets?.[PAYPAL_LOGO_KEY] && (
          <img
            className="paypal-account-detail__logo"
            src={assets[PAYPAL_LOGO_KEY]}
            alt="PayPal"
          />
        )}
      </header>

      <div className="paypal-account-detail__content">
        <PayPalCard email={account?.email} />

        <div className="paypal-account-detail__actions">
          <button
            className={`paypal-account-detail__unlink${
              isUnlinking ? ' paypal-account-detail__unlink--loading' : ''
            }`}
            onClick={() => setShowConfirm(true)}
            disabled={isUnlinking}
            type="button"
          >
            {translatedTexts.UNLINK_CONFIRM_BTN}
          </button>
        </div>

        <SelectPaymentMethodButton
          enabled={showPaymentSelector}
          onClick={handleSelect}
        />
      </div>

      <ConfirmDeleteModal
        show={showConfirm}
        handleDelete={handleUnlink}
        setShow={setShowConfirm}
        customTitle={translatedTexts.UNLINK_TITLE}
        customDescription={translatedTexts.UNLINK_DESCRIPTION}
        customConfirmText={translatedTexts.UNLINK_CONFIRM_BTN}
        maxCharsPerLine={Infinity}
      />

      <AlertModal
        open={showErrorAlert}
        type="error"
        onClose={handleCloseErrorAlert}
        messageByLines={{
          line1: translatedTexts.INTERMITTENCE_ERROR,
        }}
      />

      <AlertModal
        open={Boolean(selectErrorMessage)}
        type="error"
        onClose={clearSelectError}
        messageByLines={{
          line1: selectErrorMessage,
        }}
      />

      <ConfirmDeleteSuscriptionsModal
        show={showSuscriptionsModal}
        handleConfirm={handleConfirmSuscriptions}
        setShow={setShowSuscriptionsModal}
        customTitle={translatedTexts.SUSCRIPTIONS_TITLE}
        customDescription={translatedTexts.SUSCRIPTIONS_DESCRIPTION}
        customConfirmText={translatedTexts.SUSCRIPTIONS_CONFIRM_BTN}
        customCancelText={translatedTexts.SUSCRIPTIONS_CANCEL_BTN}
      />
    </div>
  );
};

export default PayPalAccountDetailScreen;
